import { useMemo } from "react";
import { useSession } from "../Components/Session";
import API from "../Services/API";
import Model from "./Model";

const useBusinessCoordinates = () => {
    const {business} = useSession();

    const res = Model({
        persist: true,
        stateKey: ['business', 'coordinates'],
        fetchData: () => API.get('business/coordinates').catch(() => null),
        returnDataKey: 'coordinates',
        defaultLoadingError: 'Sorry, there was an error loading business location.',
    });

    return useMemo(() => {
        if (!res?.coordinates) {
            return res;
        }

        return {
            ...res,
            coordinates: {
                ...res.coordinates,
                latitude: business?.address?.coordinates?.[0] ?? res.coordinates.latitude ?? null,
                longitude: business?.address?.coordinates?.[1] ?? res.coordinates.longitude ?? null,
            },
        }
    }, [res, business])
}

const useBusiness = (alwaysRefresh = false) => {
    const {refresh} = useSession();

    return Model({
        persist: true,
        alwaysRefresh,
        stateKey: ['business'],
        fetchData: () => API.get('business'),
        returnDataKey: 'business',
        registerAdditionalMethods: (opts) => {
            return {
                saveDetails: (data) => {
                    if (!data.postcode && data.address) {
                        data.postcode = data.address.postcode
                    }
                    return opts.wrapUpdateCall(
                        () => API.post('/business/details', data).then(refresh),
                        null,
                        true,
                    );
                },
                saveSocials: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/business/socials', data).then(refresh),
                        null,
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading business details.',
    });
}

const useGrants = () => {
    const data = Model({
        persist: true,
        stateKey: ['business', 'grants'],
        fetchData: () => API.get('business/grants').catch(() => null),
        returnDataKey: 'grants',
        registerAdditionalMethods: (opts) => {
            return {
                addUser: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/business/grants', data),
                        null,
                        true,
                    );
                },
                updateUser: (grantId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/business/grants/${grantId}`, data),
                        null,
                        true,
                    );
                },
                removeUser: (grantId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/business/grants/${grantId}`),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading business users.',
    });

    return {
        ...data,
        activeGrants: data?.grants?.filter(g => g.status === 'Active'),
        keyedByUserId: data?.grants?.reduce((acc, g) => {
            acc[g.userId] = g.firstName ? `${g.firstName} ${g.lastName}` : g.email;
            return acc;
        }, {}),
    }
}

export {
    useBusiness,
    useBusinessCoordinates,
    useGrants,
}
